import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import Box from '@mui/joy/Box';
import { CircularProgress } from '@mui/joy';
import Typography from '@mui/joy/Typography';
import { GetCustomerChannels } from '../../../axios/Axios';
import MessagesPane from './MessagesPane';
import type { ChatProps, ChannelType } from './Messages';

function getDefaultChannel(hasSms: boolean, hasWhatsapp: boolean, hasEmail: boolean): ChannelType {
    if (hasSms) return 'sms';
    if (hasWhatsapp) return 'whatsapp';
    if (hasEmail) return 'email';
    return 'sms';
}

type CustomerMessagesTabProps = {
    customerId: number;
};

export default function CustomerMessagesTab(props: CustomerMessagesTabProps) {
    const { customerId } = props;
    const [chat, setChat] = useState<ChatProps | null>(null);
    const [activeChannel, setActiveChannel] = useState<ChannelType>('sms');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (customerId <= 0) {
            setChat(null);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        GetCustomerChannels(customerId)
            .then((res) => {
                if (cancelled) return;
                const { has_sms, has_whatsapp, has_email, sender } = res.data;
                const chatObj: ChatProps = {
                    id: String(customerId),
                    sender,
                    messages: [],
                    has_sms: has_sms,
                    has_whatsapp: has_whatsapp,
                    has_email: has_email,
                };
                setChat(chatObj);
                setActiveChannel(getDefaultChannel(has_sms, has_whatsapp, has_email));
            })
            .catch(() => {
                if (!cancelled) setError('Could not load channels.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId]);

    const onChannelChange = useCallback((channel: ChannelType) => {
        setActiveChannel(channel);
    }, []);

    if (customerId <= 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography level="body-sm" color="neutral">
                    Select a lead or booking to view messages.
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography level="body-sm" color="danger">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!chat) {
        return null;
    }

    return (
        <MessagesPane
            chat={chat}
            activeChannel={activeChannel}
            onChannelChange={onChannelChange}
            inDrawer
        />
    );
}
